import api from './api';

export interface Notification {
  id: number;
  recipient_id: number;
  recipient_type: 'student' | 'consultant' | 'admin';
  title: string;
  title_ar: string;
  message: string;
  message_ar: string;
  notification_type: 
    | 'message' 
    | 'application_status' 
    | 'document_reviewed' 
    | 'document_rejected'
    | 'payment_submitted'
    | 'payment_confirmed'
    | 'staff_assigned'
    | 'action_required'
    | 'general';
  application_id?: number | null;
  chat_message_id?: number | null;
  sender_id?: number | null;
  sender_type?: 'student' | 'consultant' | 'admin' | 'system' | null;
  sender_name?: string | null;
  is_read: boolean;
  read_at?: string | null;
  extra_data?: any;
  created_at: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  unread_count: number;
}

export interface NotificationStatsResponse {
  total_notifications: number;
  unread_notifications: number;
  notification_type_counts: Record<string, number>;
  recent_notifications: Notification[];
}

export interface UnreadCountResponse {
  unread_count: number;
}

export interface MarkReadRequest {
  notification_ids: number[];
}

export interface MarkReadResponse {
  message: string;
  updated_count: number;
}

export interface DeleteResponse {
  message: string;
  deleted_count?: number;
}

class NotificationService {
  /**
   * Get notifications with optional filters
   */
  async getNotifications(params?: {
    page?: number;
    page_size?: number;
    notification_type?: string;
    is_read?: boolean;
  }): Promise<NotificationListResponse> {
    // The backend collection route is `/notifications/`. Calling the variant
    // without a slash causes FastAPI to redirect, and some reverse proxies
    // rewrite that redirect to HTTP, which browsers correctly block as CORS.
    const response = await api.get<NotificationListResponse>('/notifications/', { params });
    return response.data;
  }

  /**
   * Get notification statistics
   */
  async getStats(): Promise<NotificationStatsResponse> {
    const response = await api.get<NotificationStatsResponse>('/notifications/stats');
    return response.data;
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    const response = await api.get<UnreadCountResponse>('/notifications/unread-count');
    return response.data.unread_count;
  }

  /**
   * Mark specific notifications as read
   */
  async markAsRead(notificationIds: number[]): Promise<MarkReadResponse> {
    const response = await api.post<MarkReadResponse>('/notifications/mark-read', {
      notification_ids: notificationIds,
    });
    return response.data;
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<MarkReadResponse> {
    const response = await api.post<MarkReadResponse>('/notifications/mark-all-read');
    return response.data;
  }

  /**
   * Delete a single notification
   */
  async deleteNotification(notificationId: number): Promise<DeleteResponse> {
    const response = await api.delete<DeleteResponse>(`/notifications/${notificationId}`);
    return response.data;
  }

  /**
   * Delete all notifications
   */
  async deleteAllNotifications(): Promise<DeleteResponse> {
    const response = await api.delete<DeleteResponse>('/notifications/');
    return response.data;
  }
}

export default new NotificationService();
